import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ufn} from '@edifecs/fp-ui-lib';
import {StatefulDuplex} from '@edifecs/fp-ui-lib';
import {Subtenant} from '@app/shared/model';
import {Persistence, ShellService, StoreSync} from '@edifecs/fp-ui-shell';
import {Observable} from 'rxjs';
import {Select} from 'primeng/select';
import {SelectItem} from 'primeng/api';
import {SubtenantService} from '../services/SubtenantService';

/**
 * Component to search subtenants or see most recently selected subtenants
 * and select another subtenant.
 */
@Component({
    selector: 'app-subtenant-list',
    templateUrl: './SubtenantListComponent.html',
    styleUrls: ['./SubtenantListComponent.css'],
    standalone: false
})
export class SubtenantListComponent implements OnInit, AfterViewInit {
    /**
     * Cache key for recent subtenants.
     */
    private static readonly CACHE_KEY_RECENT_SUBTENANTS = 'subtenant.recent';

    /**
     * Maximum number of subtenants to show in list
     */
    private static readonly SUBTENANT_MAX_LIST_COUNT = 10;

    /**
     * @internal
     * Name of current subtenant
     */
    currentSubtenant?: Observable<Subtenant>;

    /**
     * @internal
     * Search clear icon visibility.
     */
    showClearIcon = false;

    /**
     * @internal
     * Recent items display status.
     */
    showingRecent = true;

    /**
     * @internal
     * Results overflow status.
     */
    hasMoreResults = false;

    /**
     * @internal
     * Callback that is called on subtenant selection.
     * Provided by external code that displays or hosts this component.
     */
    onSelectedCallback?: (subtenant: Subtenant) => void;

    /**
     * @internal
     * Subtenant search duplex.
     */
    readonly duplex: StatefulDuplex<string, Subtenant[]>;

    /**
     * @internal
     * Options for search by dropdown
     */
    readonly searchByOptions: SelectItem[] = [
        {label: 'Name', value: 'name'},
        {label: 'Id', value: 'id'}
    ];

    /**
     * @internal
     * Search by dropdown component
     */
    @ViewChild('dpdSearchBy')
    searchByDropdown?: Select;

    /**
     * @internal
     * Element ref for search input.
     */
    @ViewChild('inp')
    searchInputEl?: ElementRef<HTMLInputElement>;

    /**
     * Local cache for saving recent subtenants.
     */
    private readonly recentCache: StoreSync;

    /**
     * Constructor
     * @param subtenantSvc Subtenant service
     * @param persistence Persistence service
     */
    constructor(private readonly subtenantSvc: SubtenantService,
                private readonly persistence: Persistence) {
        // init local cache for recent subtenants
        this.recentCache = persistence.localSync;

        // setup subtenant search duplex
        this.duplex = new StatefulDuplex<string, Subtenant[]>(this.loadData.bind(this), {
            debounceInput: 300,
            latestOnly: true
        });

        // init current subtenant
        this.currentSubtenant = this.subtenantSvc.subtenantValue;
    }

    /**
     * @internal
     * Handle component init
     */
    ngOnInit(): void {
        // trigger duplex to show recent subtenants on start
        this.duplex.send('');
    }

    /**
     * @internal
     * Handle component view init
     */
    ngAfterViewInit(): void {
        // focus the search input on start
        this.searchInputEl?.nativeElement?.focus();

    }

    /**
     * @internal
     * Handle search input changes
     * @param evt Input event
     */
    onSearchInput(evt: Event): void {
        // get changed value and sent it to search duplex
        const value = evt.target instanceof HTMLInputElement ? evt.target.value : '';
        this.duplex.send(value);
    }

    /**
     * @internal
     * Handle clear icon click
     * @param inp Search input element
     */
    onClickClear(inp: HTMLInputElement): void {
        // clear input and trigger duplex with empty search to show recent subtenants
        inp.value = '';
        this.duplex.send('');
    }

    /**
     * @internal
     * Handle subtenant selection by the user
     * @param item Selected subtenant item
     */
    onSelectSubtenant(item: Subtenant): void {
        if (!ufn.isText(item.id)) {
            return;
        }

        // set subtenant in service
        this.subtenantSvc.setSubtenant(item.id);

        // push selected item to start of recent list if not present already, save list trimmed to max length
        const recent = this.recentCache.get(SubtenantListComponent.CACHE_KEY_RECENT_SUBTENANTS);
        const updated = ufn.isArray(recent) ? (recent as Subtenant[]) : [];
        if (!updated.some(i => item.id === i.id)) {
            updated.unshift(item);
            this.recentCache.put(SubtenantListComponent.CACHE_KEY_RECENT_SUBTENANTS,
                updated.slice(0, SubtenantListComponent.SUBTENANT_MAX_LIST_COUNT));
        }

        // invoke callback function if provided
        if (ufn.isFunction(this.onSelectedCallback)) {
            this.onSelectedCallback();
        }

    }

    /**
     * Perform subtenants loading from recent subtenants (if search query is empty),
     * or call subtenant API for search results.
     * @param searchText Search query
     */
    private async loadData(searchText: string): Promise<Subtenant[]> {
        // clear icon is show only when there is some search text
        this.showClearIcon = ufn.isText(searchText);

        if (ufn.isNilEmptyOrBlank(searchText)) {
            // return recent subtenants, excluding the currently selected one
            this.showingRecent = true;
            const recent = this.recentCache.get(SubtenantListComponent.CACHE_KEY_RECENT_SUBTENANTS);
            return (ufn.isArray(recent) ? recent : [])
                .filter(i => this.subtenantSvc.subtenantId !== i.id)
                .slice(0, SubtenantListComponent.SUBTENANT_MAX_LIST_COUNT);
        } else {
            // clear recent items
            this.showingRecent = false;

            // search by id or name as specified
            const searchBy = this.searchByDropdown?.value;
            let items: Subtenant[];
            if ('id' === searchBy) {
                items = await this.subtenantSvc.searchSubtenantsById(
                    searchText.replace(/\./g, '-'),
                    SubtenantListComponent.SUBTENANT_MAX_LIST_COUNT + 1);
            } else {
                items = await this.subtenantSvc.searchSubtenantsByName(searchText,
                    SubtenantListComponent.SUBTENANT_MAX_LIST_COUNT + 1);
            }

            // return configured length, detecting overflow
            this.hasMoreResults = items.length > SubtenantListComponent.SUBTENANT_MAX_LIST_COUNT;
            return items.slice(0, SubtenantListComponent.SUBTENANT_MAX_LIST_COUNT);
        }
    }

    /**
     * Show this component in side window.
     * @param shellSvc Shell service.
     */
    public static show(shellSvc: ShellService): void {
        // open subtenant list panel
        shellSvc.showModalWindow({
            align: 'left',
            alignOffset: document.getElementsByTagName('edf-side-bar')[0].clientWidth,
            content: {
                component: SubtenantListComponent,
                props: {
                    onSelectedCallback: () => {
                        // handle subtenant selection
                        shellSvc.hideModalWindow();
                        window.location.replace(window.location.origin);
                    }
                }
            }
        });
    }
}
